import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';

export interface Condition {
  field: string;
  operator: string;
  value: string | number | object | null;
  fieldError?: string;
  operatorError?: string;
  valueError?: string;
}

export const validateConditions = (
  conditions: Condition[],
  t: TGlobalFunctionTyped,
  onValidate?: (isValid: boolean) => void,
): Condition[] => {
  const updatedConditions = conditions.map((condition) => {
    let valueError =
      !condition.value &&
      !['isEmpty', 'isNotEmpty'].includes(condition.operator)
        ? t(
            'bookkeep.integrations.settings.payableRules.errors.conditions.value',
          )
        : undefined;

    // When the field is 'grossAmount', validate the value is a number
    if (
      condition.field === 'grossAmount' &&
      !['isBetween', 'isNotBetween'].includes(condition.operator)
    ) {
      valueError =
        typeof condition.value !== 'number'
          ? t(
              'bookkeep.integrations.settings.payableRules.errors.conditions.value',
            )
          : undefined;
    }
    // Additional validation for grossAmount with isBetween or isNotBetween
    // Ensure that the value from and to properties are numbers
    if (
      condition.field === 'grossAmount' &&
      ['isBetween', 'isNotBetween'].includes(condition.operator)
    ) {
      const value = condition.value as { from?: number; to?: number } | null;
      if (
        value === null ||
        typeof value.from !== 'number' ||
        typeof value.to !== 'number'
      ) {
        valueError = t(
          'bookkeep.integrations.settings.payableRules.errors.conditions.fromTo',
        );
      }
    }

    return {
      ...condition,
      fieldError: !condition.field
        ? t(
            'bookkeep.integrations.settings.payableRules.errors.conditions.field',
          )
        : undefined,
      operatorError: !condition.operator
        ? t(
            'bookkeep.integrations.settings.payableRules.errors.conditions.operator',
          )
        : undefined,
      valueError,
    };
  });

  const isValid = updatedConditions.every(
    (condition) =>
      !condition.fieldError &&
      !condition.operatorError &&
      !condition.valueError,
  );

  if (onValidate) {
    onValidate(isValid);
  }

  return updatedConditions;
};

export const filterValidConditions = (conditions: Condition[]): Condition[] => {
  return conditions.filter((condition) => {
    const isFieldValid = condition.field && condition.field.trim() !== '';
    const isOperatorValid =
      condition.operator && condition.operator.trim() !== '';
    const isValueValid =
      condition.value !== null ||
      ['isEmpty', 'isNotEmpty'].includes(condition.operator);

    if (['isBetween', 'isNotBetween'].includes(condition.operator)) {
      const value = condition.value as { from?: number; to?: number } | null;
      return (
        isFieldValid &&
        isOperatorValid &&
        value?.from !== undefined &&
        value?.to !== undefined
      );
    }

    return isFieldValid && isOperatorValid && isValueValid;
  });
};
